/**
 * API Client for chess2fen v3.1
 *
 * Handles all API communication with the FastAPI backend.
 */

import type {
  ModelsResponse,
  InferenceRequest,
  InferenceResponse,
  HealthResponse,
  APIError,
} from '@/types/api'

// API base URL - use proxy in development, absolute path in production
const API_BASE_URL = import.meta.env.DEV ? '/api' : ''

/**
 * Custom error class for API errors
 */
export class APIClientError extends Error {
  constructor(
    message: string,
    public status?: number
  ) {
    super(message)
    this.name = 'APIClientError'
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchAPI<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...options?.headers,
      },
    })

    if (!response.ok) {
      let errorMessage = `Request failed (${response.status})`

      try {
        const errorData: APIError = await response.json()
        errorMessage = errorData.detail || errorMessage
      } catch {
        // If JSON parsing fails, use status text
        errorMessage = response.statusText || errorMessage
      }

      throw new APIClientError(errorMessage, response.status)
    }

    return (await response.json()) as T
  } catch (error) {
    if (error instanceof APIClientError) {
      throw error
    }

    // Network error or other issue
    throw new APIClientError(
      error instanceof Error ? error.message : 'Network error'
    )
  }
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get health status of the API
 */
export async function getHealth(): Promise<HealthResponse> {
  return fetchAPI<HealthResponse>('/health')
}

/**
 * Get list of available models
 */
export async function getModels(): Promise<ModelsResponse> {
  return fetchAPI<ModelsResponse>('/models')
}

/**
 * Perform inference on an uploaded image
 */
export async function inferFEN(
  request: InferenceRequest,
  onProgress?: (progress: number) => void
): Promise<InferenceResponse> {
  const formData = new FormData()
  formData.append('file', request.file)

  const params = new URLSearchParams()
  if (request.model_name) {
    params.append('model_name', request.model_name)
  }

  const url = `/infer${params.toString() ? `?${params.toString()}` : ''}`

  // Use XMLHttpRequest for progress tracking if callback provided
  if (onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          onProgress(percentComplete)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch (error) {
            reject(
              new APIClientError(
                'Failed to parse response',
                xhr.status
              )
            )
          }
        } else {
          let errorMessage = `Request failed (${xhr.status})`
          try {
            const errorData = JSON.parse(xhr.responseText)
            errorMessage = errorData.detail || errorMessage
          } catch {
            errorMessage = xhr.statusText || errorMessage
          }
          reject(new APIClientError(errorMessage, xhr.status))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new APIClientError('Network error'))
      })

      xhr.open('POST', `${API_BASE_URL}${url}`)
      xhr.send(formData)
    })
  }

  // Standard fetch for no progress tracking
  return fetch(`${API_BASE_URL}${url}`, {
    method: 'POST',
    body: formData,
  }).then(async (response) => {
    if (!response.ok) {
      let errorMessage = `Request failed (${response.status})`
      try {
        const errorData: APIError = await response.json()
        errorMessage = errorData.detail || errorMessage
      } catch {
        errorMessage = response.statusText || errorMessage
      }
      throw new APIClientError(errorMessage, response.status)
    }
    return response.json()
  })
}
